/**
 * Quantity-controlled stock (tidaltech/koi#8082, #8096), backed by
 * leatherback's StockService. Quantities only change through commands,
 * each recorded as an immutable movement; there is no "set balance".
 */
import type {
  StockBalance,
  StockItem,
  StockUnit,
  StockUsage,
  WarehouseLocation,
} from './fulfillment';

export type { StockBalance, StockItem, StockUnit, StockUsage, WarehouseLocation };

export const movementReasons = [
  'OPENING_BALANCE',
  'GOODS_RECEIPT',
  'TRANSFER',
  'FULFILLMENT_ISSUE',
  'RETURN',
  'ADJUSTMENT',
] as const;
export type MovementReason = (typeof movementReasons)[number];

export const movementReasonLabels: Record<MovementReason, string> = {
  OPENING_BALANCE: 'Opening balance',
  GOODS_RECEIPT: 'Received',
  TRANSFER: 'Transfer',
  FULFILLMENT_ISSUE: 'Issued to an order',
  RETURN: 'Returned',
  ADJUSTMENT: 'Adjustment',
};

export const locationTypes = ['WAREHOUSE', 'ZONE', 'RACK', 'SHELF', 'BIN'] as const;
export type LocationType = (typeof locationTypes)[number];

export const locationTypeLabels: Record<LocationType, string> = {
  WAREHOUSE: 'Warehouse',
  ZONE: 'Zone',
  RACK: 'Rack',
  SHELF: 'Shelf',
  BIN: 'Bin',
};

export interface StockMovement {
  id: string;
  stockItemId: string;
  quantity: number;
  fromLocationId?: string | undefined;
  toLocationId?: string | undefined;
  reason: MovementReason;
  referenceType?: string | undefined;
  referenceId?: string | undefined;
  note?: string | undefined;
  actor?: string | undefined;
  createTime?: string | undefined;
}

export interface StockItemInput {
  sku: string;
  displayName: string;
  description?: string | undefined;
  stockUnit: StockUnit;
  active: boolean;
  // Replaces the item's barcodes when given; omitted leaves them unchanged.
  barcodes?: string[] | undefined;
  // '' clears it; omitted leaves it unchanged.
  usage?: StockUsage | '' | undefined;
  // 0 clears it; omitted leaves it unchanged.
  minimumQuantity?: number | undefined;
}

/** Uppercases a barcode and drops whitespace, as Leatherback stores it. */
export function normalizeBarcode(code: string): string {
  return code.replace(/\s+/g, '').toUpperCase();
}

/**
 * Barcodes typed one per line or separated by commas. Spaces inside a code
 * are dropped, since printed barcodes are often shown in groups.
 */
export function parseBarcodes(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[\n,;]+/)
        .map(normalizeBarcode)
        .filter(Boolean),
    ),
  ];
}

/** Whether a scanned code is this stock item's SKU or one of its barcodes. */
export function stockItemMatchesCode(
  item: { sku: string; barcodes: readonly string[] },
  code: string,
): boolean {
  const sku = code.trim().replace(/\s+/g, ' ').toUpperCase();
  return item.sku === sku || item.barcodes.includes(normalizeBarcode(code));
}

export interface WarehouseLocationInput {
  siteId: string;
  parentId?: string | undefined;
  type: LocationType;
  code: string;
  displayName?: string | undefined;
  active: boolean;
}

interface CommandBase {
  stockItemId: string;
  note?: string | undefined;
  /** Reused on retries so a command is never applied twice. */
  idempotencyKey: string;
}

export type StockCommand =
  | (CommandBase & {
      kind: 'receive' | 'return' | 'openingBalance';
      locationId: string;
      quantity: number;
      referenceType?: string | undefined;
      referenceId?: string | undefined;
    })
  | (CommandBase & {
      kind: 'transfer';
      fromLocationId: string;
      toLocationId: string;
      quantity: number;
    })
  | (CommandBase & { kind: 'adjust'; locationId: string; quantityDelta: number });

export type StockCommandKind = StockCommand['kind'];

export const stockCommandLabels: Record<StockCommandKind, string> = {
  receive: 'Receive',
  transfer: 'Transfer',
  return: 'Return to stock',
  adjust: 'Adjust',
  openingBalance: 'Opening balance',
};

/** Commands that change quantities without goods moving; shown with a reminder that they are recorded with the user's name. */
export const adjusterCommands: readonly StockCommandKind[] = ['adjust', 'openingBalance'];

export interface StockCommandResult {
  movement: StockMovement;
  replayed: boolean;
  balances: StockBalance[];
}

export interface StockMovementPage {
  movements: StockMovement[];
  nextPageToken?: string | undefined;
}

/** Totals across locations for one stock item. */
export function stockTotals(balances: readonly StockBalance[], stockItemId: string) {
  let onHand = 0;
  let allocated = 0;
  for (const balance of balances) {
    if (balance.stockItemId !== stockItemId) continue;
    onHand += balance.onHand;
    allocated += balance.allocated;
  }
  return { onHand, allocated, available: onHand - allocated };
}

/** True when an item with a minimum has less than that on hand. */
export function isBelowMinimum(item: StockItem, onHand: number): boolean {
  return item.minimumQuantity > 0 && onHand < item.minimumQuantity;
}

/** Signed change a movement makes at one location, or 0 if it doesn't touch it. */
export function movementDelta(movement: StockMovement, locationId: string): number {
  if (movement.toLocationId === locationId) return movement.quantity;
  if (movement.fromLocationId === locationId) return -movement.quantity;
  return 0;
}

export function validateCommand(command: StockCommand): string | null {
  const whole = (n: number) => Number.isInteger(n) && n > 0;
  switch (command.kind) {
    case 'transfer':
      if (!command.fromLocationId || !command.toLocationId) return 'Choose both locations.';
      if (command.fromLocationId === command.toLocationId) return 'Choose two different locations.';
      return whole(command.quantity) ? null : 'Quantity must be a whole number above zero.';
    case 'adjust':
      if (!command.locationId) return 'Choose a location.';
      if (!Number.isInteger(command.quantityDelta) || command.quantityDelta === 0) {
        return 'Enter how much to add or remove.';
      }
      return command.note?.trim() ? null : 'Say why the balance was wrong.';
    default:
      if (!command.locationId) return 'Choose a location.';
      return whole(command.quantity) ? null : 'Quantity must be a whole number above zero.';
  }
}
